/** Secure launch (M29, console Settings › Secure launch). Shell created by WP-M29-SCHEMA1: later work packages add keys inside their group (en and fil together, no dots inside key segments). */
export const launch = {
  page: {
    title: "Secure launch",
    subtitle: "Your server vouches for the customer. The browser only carries a one-time code that works once, for about 60 seconds.",
    unavailableTitle: "Secure launch isn't turned on for this workspace",
    unavailableBody: "Ask Sagot to turn it on. Then you can choose which sites may show your chat and set up launches from your own login.",
    loadError: "Secure launch couldn't load.",
  },
  arrival: {
    title: "How customers arrive",
    subtitle: "Per agent. Choose the agent, then the ways a customer can reach its chat.",
    agent: "Agent",
    agentHint: "Each agent has its own settings.",
    launchTitle: "Launch from your login",
    launchHint: "Your server calls the Launch API and redirects. Recommended.",
    email_codeTitle: "Email code",
    email_codeHint: "For sites without their own login. The customer gets a code by email.",
    open_linkTitle: "Open link, no sign-in",
    open_linkHint: "Anyone with the link can ask public questions. No account data is ever shown.",
    lastWayIn: "At least one way in has to stay on.",
    verifiedOnly: "Signed-in customers only",
    stateTitle: "Bind the link to the starting tab",
    stateHint: "A forwarded link opens nothing. Adds one redirect through Sagot first. Applies to every agent.",
    stateChangeTitle: "What your server changes",
    stateStep1: "Sagot sends the customer to your mint URL with a one-time state value.",
    stateStep2: "Your server signs the customer in as usual and calls the Launch API with that same state.",
    stateStep3: "The link only opens in the tab that started it, so a forwarded or copied link fails.",
    stateNeedsMint: "Add your mint URL under Your endpoints first, or customers have nowhere to start.",
    noAgentsTitle: "No agents yet",
    noAgentsBody: "Create an agent first. Arrival methods are set per agent.",
    readOnly: "Only people who can manage Secure launch can change this.",
    save: "Save arrival",
    saving: "Saving",
    discard: "Discard changes",
    saved: "Arrival methods saved.",
  },
  urls: {
    title: "Your endpoints",
    subtitle: "Launches only ever send customers to these addresses.",
    statusSet: "Mint URL set",
    statusNotSet: "Mint URL not set",
    mintLabel: "Mint URL (your server)",
    mintHint: "Used for state binding and to relaunch an expired frame. https only.",
    mintPlaceholder: "https://www.example.com/support/chat",
    returnLabel: "Allowed return URLs",
    returnHint: "Exact addresses a launch may send the customer back to, such as https://www.example.com/account. No wildcards.",
    placeholder: "https://www.example.com/account",
    add: "Add address",
    empty: "No return address yet. A launch that asks for a return address is refused until you list it.",
    count: "{{count}} of {{max}}",
    remove: "Remove {{address}}",
    duplicate: "{{address}} is already in the list.",
    full: "You can list up to {{max}} addresses.",
    added: "{{address}} added",
    removed: "{{address}} removed",
    invalid: "Enter a full address such as https://www.example.com/account.",
    invalidHttp: "Only https:// addresses are allowed.",
    invalidWildcard: "Wildcards aren't allowed. List each address exactly.",
    invalidCredentials: "Remove the user name and password from the address.",
    invalidFragment: "Remove the #fragment from the address.",
    invalidLength: "That address is too long (2048 characters at most).",
    readOnly: "Only people who can manage Secure launch can change this.",
    save: "Save endpoints",
    saving: "Saving",
    discard: "Discard changes",
    saved: "Endpoints saved.",
  },
  frame: {
    title: "Show inside your pages",
    subtitle: "Sites allowed to frame the chat. Default: none.",
    statusOn: "On",
    statusOff: "Off",
    listLabel: "Sites that may show the chat",
    hint: "Exact addresses only, such as https://www.example.com. No wildcards, no paths.",
    placeholder: "https://www.example.com",
    add: "Add site",
    empty: "No site is allowed. Nobody can show the chat inside their own page.",
    count: "{{count}} of {{max}}",
    remove: "Remove {{origin}}",
    duplicate: "{{origin}} is already in the list.",
    full: "You can list up to {{max}} sites.",
    added: "{{origin}} added",
    removed: "{{origin}} removed",
    invalid: "Enter an exact address such as https://www.example.com.",
    invalidHttp: "Only https:// sites can show the chat.",
    invalidWildcard: "Wildcards aren't allowed. List each site by its exact address.",
    invalidPath: "Use the site's address only, without a page path.",
    invalidAddress: "Use the site's name, not an IP address.",
    invalidCredentials: "Remove the user name and password from the address.",
    cspLead: "Your site adds one line to its own Content-Security-Policy:",
    cspTail: "Keep the chat off deposit and payment pages.",
    sentAs: "Sent as frame-ancestors on every chat page of this workspace. A change reaches visitors within a minute.",
    lastChanged: "Last changed {{when}}",
    neverChanged: "Not changed yet",
    save: "Save sites",
    saving: "Saving",
    discard: "Discard changes",
    saved: "Sites saved. The change reaches visitors within a minute.",
    readOnly: "Only people who can manage settings can change this list.",
    loadError: "The allowed sites couldn't load.",
    loadErrorBody: "Check your connection and try again. Nothing was changed.",
  },
  session: {
    title: "Session limits",
    subtitle: "Never longer than your own login session.",
    access: "Access token",
    idle: "Idle timeout",
    absolute: "Maximum",
    minutes_one: "{{count}} minute",
    minutes_other: "{{count}} minutes",
    hours_one: "{{count}} hour",
    hours_other: "{{count}} hours",
    limitsHint: "Access token 5 to 60 minutes, idle timeout 5 to 60 minutes, maximum 1 to 12 hours. A change applies to launches made after it.",
    urlTokenTitle: "Allow sign-in tokens in URLs",
    urlTokenHint: "The older portal sign-in (?sso_token= or #token=). Turn it off once launches are live.",
    readOnly: "Only people who can manage Secure launch can change this.",
    save: "Save limits",
    saving: "Saving",
    discard: "Discard changes",
    saved: "Session limits saved.",
  },
  keys: {
    title: "Server key",
    subtitle: "Shown once at creation. Stored as a peppered hash.",
    create: "Create launch key",
    createDefaultName: "Launch",
    emptyTitle: "No launch key yet",
    emptyBody: "Create a key with the launch scope so your server can start chats for signed-in customers.",
    loadError: "The server keys couldn't load.",
    colName: "Name",
    colScopes: "Scopes",
    colAllowed: "Allowed IPs",
    colLastUsed: "Last used",
    colExpires: "Expires",
    colActions: "Actions",
    neverUsed: "Never",
    neverExpires: "Never",
    anyAddress: "Any address",
    retires: "Retires in {{time}}",
    signed: "Signed requests",
    rotate: "Rotate",
    rotateOne: "Rotate {{name}}",
    security: "Security",
    securityOne: "IP allowlist and signing for {{name}}",
    deniedTitle: "Server keys are managed by owners and admins",
    deniedBody: "Ask someone who can manage API keys to create or rotate the launch key.",
    note: "Keep this key on your server. Add your server's addresses so a stolen key is useless elsewhere.",
  },
  test: {
    title: "Test launch",
    subtitle: "Sandbox only. Uses a made-up test customer.",
    agent: "Agent",
    tier: "Customer tier",
    noTier: "No tier",
    noTiers: "No tiers are defined for this workspace yet.",
    plusOnly: "Customer facts need Secure launch Plus.",
    status: "Account status",
    statuses: {
      active: "Active",
      restricted: "Restricted",
      suspended: "Suspended",
      self_excluded: "Self-excluded",
      closed: "Closed",
      kyc_pending: "Verification pending",
    },
    displayName: "Display name",
    displayNamePlaceholder: "Test Customer",
    run: "Run test launch",
    runAgain: "Run again",
    sandboxNote: "Creates a test customer with a reference like test_ab12cd34. It shows in the audit log as a test launch and is not counted in launch metrics.",
    noLiveTitle: "No live agent to test with",
    noLiveBody: "Publish an agent first. A launch opens the chat of a live agent.",
    resultLabel: "What crossed the browser",
    stepMint: "Your server asks Sagot for a launch",
    mintNote: "The customer's reference travels in this request body only.",
    stepBrowser: "The browser is sent to this address",
    browserNote: "The code sits after the # so it never reaches a server log or a referrer. It works once, for about a minute.",
    open: "Open the chat",
    copyLink: "Copy link",
    nothingPersonal: "Checked: the test customer's reference is not in this address.",
    stepRedeem: "The chat page redeems the code",
    status_issued: "Waiting",
    status_redeemed: "Redeemed",
    status_expired: "Expired",
    status_revoked: "Revoked",
    waiting_one: "Waiting for the link to be opened. {{count}} second left.",
    waiting_other: "Waiting for the link to be opened. {{count}} seconds left.",
    waitingEnding: "The code is about to expire.",
    expiredUnused: "The code expired before anyone opened it. Run the test again.",
    revoked: "This launch was ended before it was used.",
    redeemedAt: "Redeemed {{when}} from {{origin}}.",
    originUnknown: "an unknown origin",
    redeemBody: "What the chat page sent",
    stepSession: "A session opens",
    sessionPending: "The session appears here once the code is redeemed.",
    sessionAccess: "Access token valid until",
    sessionIdle: "Ends after",
    sessionIdleValue_one: "{{count}} minute without activity",
    sessionIdleValue_other: "{{count}} minutes without activity",
    sessionMax: "Ends no later than",
    statusError: "The test status couldn't be checked. Run the test again.",
  },
  errors: {},
  tier: {
    title: "Your Secure launch",
    subtitle_lite: "Lite signs customers in and names them in chat. Customer facts need Plus.",
    subtitle_plus: "Plus signs customers in and uses what you know about them.",
    name_none: "Not included",
    name_lite: "Lite",
    name_plus: "Plus",
    usageLabel: "Launches this month",
    resets: "Counts again from {{date}} (Manila time).",
    usedUp: "All launches this month are used. New launches are refused until {{date}}. Customers already signed in keep chatting.",
    almostUsed_one: "{{count}} launch left this month. The count starts again on {{date}}.",
    almostUsed_other: "{{count}} launches left this month. The count starts again on {{date}}.",
    unlimited: "Unlimited launches. Customer facts are on.",
    includesLabel: "What your Secure launch includes",
    included: "Included",
    notIncluded: "Not included",
    lineLaunch: "Signed-in launch with a one-time code, session limits and revoke",
    lineNamed: "\"Chatting as\" the customer, with chats kept together by customer reference",
    lineFacts: "Customer facts (tier, account status, attributes) used by the AI, flows and routing",
    promptTitle: "Customer facts need Secure launch Plus",
    promptBody: "A launch that sends a tier, an account status or attributes is refused (403 secure_launch_plus_required), and facts already stored are not shown to the AI or used by flows and routing. Upgrade to Plus to use them. Plus is unlimited and replaces Lite.",
    upgrade: "Upgrade to Plus",
    askOwner: "Ask a workspace owner or admin to upgrade to Secure launch Plus.",
  },
  upsell: {
    title: "Sign customers in from your own login",
    badge: "Add-on",
    body: "Secure launch lets your own server vouch for a signed-in customer, so the chat opens already signed in with that customer's tier and status.",
    pointVouch: "Your server vouches for the customer. Nobody types a code or a password.",
    pointCode: "The browser only carries a one-time code that works once, for about a minute.",
    pointTest: "Choose how customers arrive, set session limits and rehearse with a test launch before go-live.",
    plans: "Included in the Institution plan. On Standard and Pro it is an add-on: Lite (PHP 2,500 a month) signs customers in, Plus (PHP 5,000 a month) also uses customer facts.",
    addAddon: "Add Secure launch",
    talkToSales: "Talk to sales",
    salesSubject: "Secure launch add-on",
    askOwner: "Ask a workspace owner or admin to add Secure launch.",
  },
  oidc: {
    title: "Sign in with your OpenID provider",
    subtitle: "If your company already runs an OpenID Connect provider, customers can arrive through it. The chat opens signed in with the customer, tier and status your provider names.",
    statusNotSet: "Not set up",
    statusOn: "On",
    statusOff: "Off",
    issuerLabel: "Issuer",
    issuerHint: "The https address of your provider. Sagot reads its discovery document from there.",
    issuerPlaceholder: "https://login.your-company.com",
    clientIdLabel: "Client ID",
    secretLabel: "Client secret",
    secretHint: "From your provider. Sagot keeps it sealed and never shows it again.",
    secretStored: "A secret is stored. Leave this empty to keep it.",
    secretKeep: "Stored",
    scopesLabel: "Extra scopes",
    scopesHint: "Separated by spaces. openid is always asked for. Up to {{max}} in all.",
    mappingTitle: "Which claims describe the customer",
    mappingHint: "Names of claims in the ID token. A tier your workspace does not define, or a status Sagot does not know, is left out and the customer still signs in.",
    mapRef: "Customer reference claim",
    mapTier: "Tier claim",
    mapStatus: "Account status claim",
    activeTitle: "Offer this sign-in",
    activeHint: "Turn it off to hide the button without losing the settings.",
    addressesTitle: "Addresses for your provider",
    addressesHint: "Register the sign-in-complete address at your provider, one per agent. Link your site to the start address.",
    addressesEmpty: "No live agent yet. Publish an agent to get its addresses.",
    redirectLabel: "Register this redirect address",
    startLabel: "Start address (link customers here)",
    copy: "Copy",
    frameNote: "Inside a frame the provider cannot be shown, so the chat falls back to launch codes there. Use your own login to mint a launch for embedded chats.",
    readOnly: "You can read these settings. Ask a workspace owner or admin to change them.",
    discard: "Discard",
    save: "Save",
    saving: "Saving…",
    saved: "OpenID sign-in saved.",
    loadError: "The OpenID sign-in settings couldn't load.",
    problemIssuerInvalid: "Enter the address of your provider, starting with https://.",
    problemIssuerHttp: "The issuer must start with https://.",
    problemClientId: "Enter the client ID from your provider.",
    problemSecret: "Enter the client secret from your provider.",
    problemScopes: "Too many scopes.",
    problemMapping: "Name a claim for each of the three.",
  },
} as const;
